/**
 * `POST /handles/resolve` (FAME-443) — reverse-resolve opaque handles to
 * user ids. Every identity costanza hands out (DM peers, group
 * owner/members, message senders, notice by/member) is a handle; the user id
 * from here is what the Attensi directory understands.
 *
 * Returns a map of only the handles that resolved. A handle absent from the
 * response has no mapping — an erased/offboarded user — and renders as a
 * former member, not an error. The mapping is immutable, so callers cache
 * results indefinitely.
 */
import { bearer, chatBaseUrl, chatFetch } from "./helpers"

/**
 * `POST /handles` (FAME-449) — the forward direction: ensure a handle exists
 * for each given user id and return them (`{user_id: handle}`). Create-or-
 * return, so starting a DM with someone who has never opened chat works —
 * same semantics the server applies when adding a group member. Used to
 * address a NEW 1:1: the picker knows people by user id, but the DM JID is
 * `<handle>@<tenant>`.
 */
export const ensureHandles = (
  sessionToken: string,
  userIds: string[],
  signal?: AbortSignal
): Promise<Record<string, string>> =>
  chatFetch<Record<string, string>, "json">({
    baseUrl: chatBaseUrl(),
    path: "/handles",
    method: "POST",
    headers: bearer(sessionToken),
    data: { user_ids: userIds },
    responseType: "json",
    transform: (data) => data as Record<string, string>,
    signal,
  })

export const resolveHandles = (
  sessionToken: string,
  handles: string[],
  signal?: AbortSignal
): Promise<Record<string, string>> =>
  chatFetch<Record<string, string>, "json">({
    baseUrl: chatBaseUrl(),
    path: "/handles/resolve",
    method: "POST",
    headers: bearer(sessionToken),
    data: { handles },
    responseType: "json",
    transform: (data) => data as Record<string, string>,
    signal,
  })
